import { Steps, Tooltip, Typography, theme } from "antd";
import type { StepProps } from "antd";
import {
  CheckCircleOutlined,
  CalendarOutlined,
  BuildOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  UndoOutlined,
  EditOutlined,
  LikeOutlined,
  MergeCellsOutlined,
  ExperimentOutlined,
  CloudUploadOutlined,
  CloudOutlined,
  FileAddOutlined,
  PauseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { CSSProperties } from "react";
import {
  Stage,
  WorkflowTemplate,
  StatusHistoryItem,
  StatusValue,
} from "./types";
import { generateStagesFromTemplate } from "./utils";
import { formatDuration } from "src/utils/duration";

const { Text } = Typography;

// Icon mapping function for each status
export const getStatusIcon = (status: StatusValue) => {
  const iconMap = {
    Open: <FileAddOutlined />,
    "To Do": <PauseCircleOutlined />,
    "In Progress": <ClockCircleOutlined />,
    Done: <CheckCircleOutlined />,
    Backlog: <InboxOutlined />,
    "Sprint Planning": <CalendarOutlined />,
    "In Review": <EyeOutlined />,
    "Code Review": <CodeOutlined />,
    Testing: <ExperimentOutlined />,
    Draft: <EditOutlined />,
    "Changes Requested": <UndoOutlined />,
    Approved: <LikeOutlined />,
    Merged: <MergeCellsOutlined />,
    Build: <BuildOutlined />,
    Test: <ExperimentOutlined />,
    Staging: <CloudUploadOutlined />,
    Production: <CloudOutlined />,
  } as const;

  return iconMap[status];
};

//-------------------------------------------------------------
// Builds the stage progress component. Tries to do so dynamically based on the status history
// Open -> Started -> In Progress -> Done
// Open -> Started -> In Progress -> Testing -> Done
// Open -> Started -> In Progress -> Testing -> Staging -> Done
// Open -> Started -> In Progress -> Testing -> Staging -> Production -> Done
// ------------------------------------------------------------

export interface StageProgressProps {
  // Either provide stages directly
  stages?: Stage[];
  // Or provide a template and current status
  template?: WorkflowTemplate;
  currentStatus?: string;
  statusHistory?: StatusHistoryItem[];
  // Common props
  direction?: "horizontal" | "vertical";
  size?: "small" | "default";
  showTimestamps?: boolean;
  showDuration?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const StageProgress: React.FC<StageProgressProps> = ({
  stages: providedStages,
  template,
  currentStatus,
  statusHistory,
  direction = "horizontal",
  size = "default",
  showTimestamps = true,
  showDuration = true,
  className,
  style,
}) => {
  const { token } = theme.useToken();

  // If stages are not provided directly, generate them from template and status
  const stages =
    providedStages ||
    generateStagesFromTemplate(template, currentStatus, statusHistory);

  const items: StepProps[] = stages.map((stage) => {
    const description = (
      <>
        {showTimestamps && stage.timestamp && (
          <div>
            <ClockCircleOutlined />{" "}
            {new Date(stage.timestamp).toLocaleDateString()}
          </div>
        )}
        {showDuration && stage.duration && (
          <Text type="secondary">
            {formatDuration(stage.duration, { style: "long", maxParts: 2 })}
          </Text>
        )}
        {stage.description && <div>{stage.description}</div>}
      </>
    );

    // Get the appropriate icon for the stage
    const icon = getStatusIcon(stage.name as StatusValue);

    return {
      title: (
        <Tooltip
          title={
            <div>
              <div>{stage.name}</div>
              {description}
            </div>
          }
        >
          {stage.name}
        </Tooltip>
      ),
      description: description,
      status:
        stage.status === "completed"
          ? "finish"
          : stage.status === "current"
          ? "process"
          : "wait",
      icon: icon,
    };
  });

  return (
    <Steps
      className={className}
      style={style}
      direction={direction}
      size={size}
      items={items}
      current={stages.findIndex((s) => s.status === "current")}
    />
  );
};
